import React, { Component } from "react";
import { InfoBox, MomentDate, SelectDropdown, Tooltip } from "../widgets";
import { humanize } from "../../utils/alerts";
import "./AlertsListTable.css";
import { getDateAsString } from "../widgets/Moment";
import Permissions, { hasPermission } from "../../permissions";
import { relativeTimeDuration } from "../../utils/formatting";
import HappyBatteries from "./happy_batteries.svg";

const NoAlerts = ({ type }) => (
  <div className="alerts-no-alerts">
    {type === "fleet" ? (
      <>
        <div className="message-heading">Great! All nodes are healthy</div>
        <div className="message">Looks like there are no issues with any nodes in your Fleet</div>
      </>
    ) : (
      <>
        <div className="message-heading">Great! This node is healthy</div>
        <div className="message">Looks like there have been no issues with this node</div>
      </>
    )}
    <img
      className="happy-batteries-img"
      src={HappyBatteries}
      alt="Happy batteries enjoying no alerts!"
    />
  </div>
);

class AlertsListTable extends Component {
  handleOnRowClick = (event, deploymentUid) => {
    event.stopPropagation();
    this.props.history.push(this.getUserUrl(deploymentUid));
  };

  getUserUrl = deploymentUid => {
    return `/fleet-monitoring/nodes/${deploymentUid}`;
  };

  getAlertStatusDisplay = alert => {
    let icon = "";
    let colour = "#919191";

    switch (this.getAlertStatus(alert)) {
      case "Resolved": {
        return (
          <span className="resolved-container">
            <i className="fa fa-check-circle resolved-icon" />
            Resolved
          </span>
        );
      }
      case "Silenced": {
        icon = "bell-slash";
        break;
      }
      default: {
        icon = "exclamation-triangle";
        colour = "red";
      }
    }
    return (
      <span>
        <i className={`fa fa-${icon}`} style={{ color: colour }} /> {this.getAlertStatus(alert)}
      </span>
    );
  };

  getAlertStatus = alert => {
    if (alert.resolvedTimestamp != null) {
      return "Resolved";
    }
    if (alert.silencedBy != null) {
      return "Silenced";
    }
    return "Open";
  };

  getDropdownOptions = alert => {
    if (this.getAlertStatus(alert) === "Silenced") {
      return [{ value: "UNSILENCE", label: "Unsilence" }];
    }
    return [{ value: "SILENCED", label: "Silence" }];
  };

  getTooltipContent = a => {
    switch (this.getAlertStatus(a)) {
      case "Resolved": {
        let resolvedTime = new Date(a.resolvedTimestamp * 1000);
        return `This alert was resolved at ${getDateAsString(
          resolvedTime,
          this.props.timezone,
          "D MMM YY, h:mm a"
        )}`;
      }
      case "Silenced": {
        let silencedTime = new Date(a.silencedTimestamp * 1000);
        return `This alert was silenced by ${a.silencedBy} on ${getDateAsString(
          silencedTime,
          this.props.timezone,
          "D MMM YY, h:mm a"
        )}`;
      }
      default: {
        return "";
      }
    }
  };

  displayTooltip = a => {
    switch (this.getAlertStatus(a)) {
      case "Resolved": {
        return true;
      }
      case "Silenced": {
        return true;
      }
      default: {
        return false;
      }
    }
  };

  render() {
    const { alerts, loading, alertCounts } = this.props;
    const total = alertCounts.total;

    const allSilenced =
      this.props.alertCounts.silenced === this.props.alertCounts.total &&
      this.props.alertCounts.total !== 0;
    const noAlerts = !alerts || alerts.length === 0;
    if (
      !loading &&
      noAlerts &&
      !allSilenced &&
      (this.props.filters === null || this.props.filters === undefined)
    ) {
      return <NoAlerts type={this.props.noAlertsType} />;
    }
    if (noAlerts && (allSilenced || this.props.filters !== null)) {
      return (
        <InfoBox type="info">
          There are no issues visible with your currently selected filter. Please change your filter
          to see {total} issue{total > 1 ? "s" : ""}.
        </InfoBox>
      );
    }

    return (
      <table className="alerts-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Description</th>
            <th>Date</th>
            <th>
              Resolution{" "}
              <Tooltip
                id={
                  hasPermission(this.props.user.permissions, Permissions.fleet.status.alerts.edit)
                    ? "alerts.list.edit"
                    : "alerts.list.view"
                }
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {alerts.map(a => {
            return (
              <tr>
                <td onClick={e => this.handleOnRowClick(e, a.deploymentUid)} className="alerts-row">
                  {this.props.alertTypeOptions[a.type]
                    ? this.props.alertTypeOptions[a.type].label
                    : humanize(a.type)}
                </td>
                <td onClick={e => this.handleOnRowClick(e, a.deploymentUid)}>{a.description}</td>
                <td onClick={e => this.handleOnRowClick(e, a.deploymentUid)}>
                  <MomentDate
                    date={new Date(a.timestamp * 1000)}
                    timezone={this.props.timezone}
                    format="D MMM YY, h:mm a"
                  />
                  {a.mostRecentlySeenTimestamp !== null && (
                    <div>
                      <span className="last-seen-label">
                        Last Detected:{" "}
                        {relativeTimeDuration(
                          a.mostRecentlySeenTimestamp,
                          this.props.timezone,
                          "D MMM YY, h:mm a"
                        )}
                      </span>
                    </div>
                  )}
                </td>
                <td>
                  <div className="status-container">
                    {!hasPermission(
                      this.props.user.permissions,
                      Permissions.fleet.status.alerts.edit
                    ) || this.getAlertStatus(a).toUpperCase() === "RESOLVED" ? (
                      this.getAlertStatusDisplay(a)
                    ) : (
                      <SelectDropdown
                        placeholder={this.getAlertStatusDisplay(a)}
                        value={this.getAlertStatus(a).toUpperCase()}
                        onChange={val => this.props.setAlertSilence(val, a)}
                        options={this.getDropdownOptions(a)}
                      />
                    )}
                    {this.displayTooltip(a) && <Tooltip content={this.getTooltipContent(a)} />}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }
}

export default AlertsListTable;
